import React, { useState, useEffect, useRef } from 'react';
import './MusicPlayer.css';

const MusicPlayer = ({ currentCharacter }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentDanteTrack, setCurrentDanteTrack] = useState(0);
  const [volume, setVolume] = useState(50);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isRepeat, setIsRepeat] = useState(false);
  const audioRef = useRef(null);

  const danteTracks = [
    { src: '/music/dante-theme-3.mp3.mp3', name: 'Bless Me With The Leaf' },
    { src: '/music/dante-theme-2.mp3', name: 'Taste The Blood' },
    { src: '/music/dante-theme.mp3' , name : 'Bless me with the leaf v1'}
  ];

  const vergilTracks = [
    { src: '/music/Bury the Light but with MORE POWER!!!.mp3', name: 'Bury The Light' }
  ];

  // Initialize audio on component mount
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume / 100;

    // Update time and duration
    const updateTime = () => {
      setCurrentTime(audioRef.current.currentTime);
    };

    const updateDuration = () => {
      setDuration(audioRef.current.duration);
    };

    audioRef.current.addEventListener('timeupdate', updateTime);
    audioRef.current.addEventListener('loadedmetadata', updateDuration);

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateTime);
        audioRef.current.removeEventListener('loadedmetadata', updateDuration);
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle track end - play next track or repeat
  useEffect(() => {
    if (!audioRef.current) return;

    const handleEnded = () => {
      if (isRepeat) {
        // Repeat current track
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      } else if (currentCharacter === 'dante') {
        const nextTrack = (currentDanteTrack + 1) % danteTracks.length;
        setCurrentDanteTrack(nextTrack);
      } else {
        // For Vergil, just loop the single track
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    };

    audioRef.current.addEventListener('ended', handleEnded);

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [currentCharacter, currentDanteTrack, isRepeat]);

  // Handle character change - switch music
  useEffect(() => {
    if (!audioRef.current) return;

    const track = currentCharacter === 'dante' 
      ? danteTracks[currentDanteTrack].src 
      : vergilTracks[0].src;

    audioRef.current.src = track;
    audioRef.current.muted = isMuted;

    if (isPlaying) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [currentCharacter]);

  // Handle Dante track change
  useEffect(() => {
    if (!audioRef.current || currentCharacter !== 'dante') return;

    audioRef.current.src = danteTracks[currentDanteTrack].src;
    audioRef.current.muted = isMuted;

    if (isPlaying) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [currentDanteTrack]);

  // Handle mute toggle
  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    if (audioRef.current) audioRef.current.muted = newMutedState;
  };

  // Handle play/pause
  const togglePlay = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);

    if (!audioRef.current) return;

    if (newPlayingState) {
      const track = currentCharacter === 'dante' 
        ? danteTracks[currentDanteTrack].src 
        : vergilTracks[0].src;
      
      if (!audioRef.current.src.includes(track)) {
        audioRef.current.src = track;
      }
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    } else {
      audioRef.current.pause();
    }
  };

  // Handle next track
  const nextTrack = () => {
    if (currentCharacter === 'dante') {
      const next = (currentDanteTrack + 1) % danteTracks.length;
      setCurrentDanteTrack(next);
    }
  };

  // Handle previous track
  const prevTrack = () => {
    if (currentCharacter === 'dante') {
      const prev = currentDanteTrack === 0 ? danteTracks.length - 1 : currentDanteTrack - 1;
      setCurrentDanteTrack(prev);
    }
  };

  const getCurrentTrackName = () => {
    if (currentCharacter === 'dante') {
      return danteTracks[currentDanteTrack].name;
    }
    return vergilTracks[0].name;
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  // Handle seek change
  const handleSeekChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  // Toggle repeat
  const toggleRepeat = () => {
    setIsRepeat(!isRepeat);
  };

  // Format time (seconds to mm:ss)
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`music-player ${currentCharacter}`}>
      <div className="music-controls">
        {/* Previous button - only for Dante */}
        {currentCharacter === 'dante' && (
          <button 
            className="music-btn nav-btn" 
            onClick={prevTrack}
            title="Previous Track"
          >
            <span style={{fontSize: '16px', fontWeight: 'bold'}}>«</span>
          </button>
        )}

        <button 
          className="music-btn play-btn" 
          onClick={togglePlay}
          title={isPlaying ? 'Pause Music' : 'Play Music'}
        >
          {isPlaying ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>

        {/* Next button - only for Dante */}
        {currentCharacter === 'dante' && (
          <button 
            className="music-btn nav-btn" 
            onClick={nextTrack}
            title="Next Track"
          >
            <span style={{fontSize: '16px', fontWeight: 'bold'}}>»</span>
          </button>
        )}

        {/* Repeat button */}
        <button 
          className={`music-btn repeat-btn ${isRepeat ? 'active' : ''}`}
          onClick={toggleRepeat}
          title={isRepeat ? 'Repeat: On' : 'Repeat: Off'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
          </svg>
        </button>

        <button 
          className="music-btn mute-btn" 
          onClick={toggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          )}
        </button>
      </div>

      {/* Seek bar */}
      <div className="seek-container">
        <span className="time-label">{formatTime(currentTime)}</span>
        <input 
          type="range" 
          className="seek-bar"
          min="0"
          max={duration || 0}
          step="0.1"
          value={currentTime}
          onChange={handleSeekChange}
        />
        <span className="time-label">{formatTime(duration)}</span>
      </div>

      {/* Volume control */}
      <div className="volume-container">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="volume-icon">
          <path d="M3 9v6h4l5 5V4L7 9H3z"/>
        </svg>
        <input 
          type="range" 
          className="volume-bar"
          min="0"
          max="100"
          value={volume}
          onChange={handleVolumeChange}
        />
        <span className="volume-label">{volume}%</span>
      </div>

      <span className="music-label">
        {getCurrentTrackName()}
      </span>
    </div>
  );
};

export default MusicPlayer;
